import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { QrCode, CreditCard, IdentificationCard, UserCircle, ArrowRight, Lightning, Bell, MapPin, FileText } from "@phosphor-icons/react";
import { passengerApi, getPassengerUser, clearPassengerSession } from "@/lib/passengerApi";
import { MajheBrand } from "@/components/majhe/MajheLayout";

export default function MajheHome() {
  const user = getPassengerUser();
  const [tickets, setTickets] = useState([]);
  const [passes, setPasses] = useState([]);
  const [cards, setCards] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    passengerApi.get("/me/tickets?status=issued").then(({ data }) => setTickets(data));
    passengerApi.get("/me/passes").then(({ data }) => setPasses(data));
    passengerApi.get("/me/cards").then(({ data }) => setCards(data));
    passengerApi.get("/service-alerts")
      .then(({ data }) => setAlerts(data?.alerts || []))
      .catch(() => setAlerts([]));
  }, []);

  const activeTicket = tickets.find((t) => t.status === "issued") || tickets[0];
  const activePass = passes.find((p) => p.is_valid) || passes[0];
  const primaryCard = cards[0];

  // Topmost alert — critical first, then warning. Used for the home banner.
  const sevRank = { critical: 0, warning: 1, info: 2 };
  const topAlert = [...alerts].sort((a, b) =>
    (sevRank[a.severity] ?? 9) - (sevRank[b.severity] ?? 9)
  )[0];
  const totalAlerts = alerts.length;

  return (
    <div data-testid="majhe-home">
      {/* Greeting + brand */}
      <div className="px-5 pt-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[12px] text-[#7B7390] font-semibold">Welcome back 👋</div>
            <div className="text-[22px] font-extrabold tracking-tight text-[#18142A] mt-0.5">
              {user?.name || `+91 ${user?.mobile}`}
            </div>
          </div>
          <button onClick={() => navigate("/majhe/profile")} data-testid="home-profile-btn"
            aria-label="Open my profile"
            className="w-11 h-11 rounded-full bg-[#EDE5FF] text-[#5B2EE5] flex items-center justify-center">
            <UserCircle size={26} weight="fill" aria-hidden="true"/>
          </button>
        </div>
        <div className="mt-2"><MajheBrand/></div>
      </div>

      {/* Active ticket / pass strip */}
      {(activeTicket || activePass) && (
        <div className="px-5 mt-5 grid gap-2">
          {activeTicket && (
            <Link to={`/majhe/tickets/${activeTicket.ticket_no}`} data-testid="home-active-ticket"
              className="flex items-center gap-3 bg-[#F5F2FF] border border-[#DCC9FF] rounded-xl px-4 py-3">
              <div className="w-9 h-9 rounded-lg bg-[#5B2EE5] text-white flex items-center justify-center">
                <QrCode size={18} weight="bold"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-[#4920C2] font-bold">Active Ticket</div>
                <div className="text-sm font-bold text-[#18142A] truncate">{activeTicket.route_name}</div>
              </div>
              <ArrowRight size={18} className="text-[#5B2EE5]"/>
            </Link>
          )}
          {activePass && (
            <Link to="/majhe/pass" data-testid="home-active-pass"
              className="flex items-center gap-3 bg-[#EDE5FF] border border-[#B89BFF] rounded-xl px-4 py-3">
              <div className="w-9 h-9 rounded-lg bg-[#2D1170] text-white flex items-center justify-center">
                <IdentificationCard size={18} weight="bold"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-[#2D1170] font-bold">Active Pass</div>
                <div className="text-sm font-bold text-[#18142A] truncate">
                  {activePass.category} · {typeof activePass.trips_left_today === "number"
                    ? `${activePass.trips_left_today} trips left today`
                    : "Unlimited trips"}
                </div>
              </div>
              <ArrowRight size={18} className="text-[#2D1170]"/>
            </Link>
          )}
        </div>
      )}

      {/* Service-alert banner — surfaces top live alert + link to all */}
      {topAlert && (
        <button onClick={() => navigate("/majhe/alerts")}
          data-testid="home-alerts-banner"
          aria-label={`${totalAlerts} service alert${totalAlerts !== 1 ? "s" : ""} live. Tap to view all.`}
          className={`mx-5 mt-5 w-[calc(100%-2.5rem)] text-left rounded-xl px-4 py-3 flex items-start gap-3 border-2
            ${topAlert.severity === "critical"
              ? "bg-red-50 border-red-300 text-red-900"
              : topAlert.severity === "warning"
                ? "bg-amber-50 border-amber-300 text-amber-900"
                : "bg-[#F5F2FF] border-[#DCC9FF] text-[#18142A]"}`}>
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0
            ${topAlert.severity === "critical"
              ? "bg-red-500 text-white"
              : topAlert.severity === "warning"
                ? "bg-amber-500 text-white"
                : "bg-[#5B2EE5] text-white"}`}>
            <Bell size={18} weight="fill"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">
              {totalAlerts} live alert{totalAlerts !== 1 ? "s" : ""} · {topAlert.severity}
            </div>
            <div className="text-[13.5px] font-extrabold leading-snug truncate">{topAlert.title}</div>
            {topAlert.affected_routes?.length > 0 && (
              <div className="text-[11px] opacity-80 mt-0.5">
                Routes: {topAlert.affected_routes.slice(0, 4).join(", ")}
                {topAlert.affected_routes.length > 4 && ` +${topAlert.affected_routes.length - 4} more`}
              </div>
            )}
          </div>
          <ArrowRight size={18} weight="bold"/>
        </button>
      )}

      {/* Big hero tiles - matching screenshots */}
      <div className="px-5 mt-5 space-y-3.5" data-testid="home-action-tiles">
        <ActionTile
          tid="tile-book"
          to="/majhe/book/quick"
          icon={<QrCode size={32} weight="bold"/>}
          title="QR Bus Booking"
          desc="Book your bus ticket instantly. Fast, easy & secure."
        />
        <ActionTile
          tid="tile-card"
          to="/majhe/card"
          icon={<CreditCard size={32} weight="bold"/>}
          title="Card Details & Recharge"
          desc="View card details, balance and recharge your card instantly."
        />
        <ActionTile
          tid="tile-pass"
          to="/majhe/pass"
          icon={<IdentificationCard size={32} weight="bold"/>}
          title="Smart Pass"
          desc={activePass
            ? `${activePass.trips_today}/${typeof activePass.daily_cap === "number" ? activePass.daily_cap : "∞"} trips today`
            : "Apply, renew & check your KTCL travel pass."}
        />
        <ActionTile
          tid="tile-track"
          to="/majhe/tickets"
          icon={<MapPin size={32} weight="bold"/>}
          title="My Tickets & Tracking"
          desc="See active QR tickets and track bus arrival in text-mode."
        />
        <ActionTile
          tid="tile-applications"
          to="/majhe/applications"
          icon={<FileText size={32} weight="bold"/>}
          title="Track My Application"
          desc="See status of your Pass / Card application & collection depot."
        />
        <ActionTile
          tid="tile-alerts"
          to="/majhe/alerts"
          icon={<Bell size={32} weight="bold"/>}
          title="Service Alerts & Diversions"
          desc={totalAlerts > 0
            ? `${totalAlerts} live alert${totalAlerts !== 1 ? "s" : ""} affecting routes right now`
            : "Latest KTCL public notices, route changes & advisories"}
        />
      </div>

      {/* Mascot illustration zone */}
      <div className="mt-8 mb-2 px-2">
        <div className="rounded-3xl p-5 text-center" style={{
          background: "linear-gradient(180deg, #FBFAFF 0%, #ECE5FF 100%)",
        }}>
          <div className="text-[26px]">🚌</div>
          <div className="text-[#5B2EE5] font-bold mt-1 text-sm">Smart Goa Transit</div>
          <div className="text-[11.5px] text-[#7B7390] mt-1 leading-relaxed">
            All your KTCL services in one place. Login again with the same number anytime — your data stays safe with us.
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionTile({ to, icon, title, desc, tid }) {
  return (
    <Link to={to} data-testid={tid} className="majhe-tile"
      aria-label={`${title}. ${desc}`}>
      <div className="majhe-tile__icon" aria-hidden="true">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="majhe-tile__title">{title}</div>
        <div className="majhe-tile__desc">{desc}</div>
      </div>
      <div className="mt-1" aria-hidden="true"><ArrowRight size={20} weight="bold" style={{ opacity: 0.7 }}/></div>
    </Link>
  );
}
